import { MEMBER_ROLES, PERSON_ROLES, ACCOUNT_TYPES, CLASSIFICATIONS, CADENCES, RECURRING_KINDS, LOAN_PREPAYMENT_MODES, LINE_KINDS, EDIT_SCOPES, LINE_STATUSES, MONTH_STATUSES, LOAN_TYPES, LOAN_RATE_TYPES, LOAN_STATUSES, LOAN_PAYMENT_KINDS, DEPOSIT_TYPES, DEPOSIT_STATUSES, HAND_LOAN_DIRECTIONS, HAND_LOAN_STATUSES, GOAL_STATUSES, NOTIFICATION_TYPES, NOTIFICATION_CHANNELS, EMAIL_DIGEST_MODES, DEFAULT_PALETTE, oklchToInt, slotColor, getPalette } from './chunk-CRYUOAYG.js';
export { ACCENT_COLORS, ACCENT_COLOR_IDS, ACCOUNT_TYPES, ACTIVITY_EVENTS, BASE_COLORS, CADENCES, CANVAS_ANIMATIONS, CANVAS_PATTERNS, CLASSIFICATIONS, DEFAULT_ACCENT_COLOR, DEFAULT_BUCKETS, DEFAULT_CANVAS_SETTINGS, DEFAULT_CURRENCY, DEFAULT_HOUSEHOLD_SETTINGS, DEFAULT_LOCALE, DEFAULT_PALETTE, DEFAULT_PREFERENCES, DEFAULT_TIMEZONE, DEPOSIT_STATUSES, DEPOSIT_TYPES, EDIT_SCOPES, EMAIL_DIGEST_MODES, EVENT_CATALOG, EVENT_GROUPS, FILL_TEXT, GOAL_STATUSES, HAND_LOAN_DIRECTIONS, HAND_LOAN_STATUSES, HOUSEHOLD_FONTS, LIGHT_BASE_COLORS, LINE_KINDS, LINE_STATUSES, LOAN_PAYMENT_KINDS, LOAN_PREPAYMENT_MODES, LOAN_RATE_TYPES, LOAN_STATUSES, LOAN_TYPES, MEMBER_ROLES, MONTH_STATUSES, NOTIFICATION_CHANNELS, NOTIFICATION_TYPES, PALETTES, PALETTE_IDS, PALETTE_SLOTS, PERSON_ROLES, RECURRENCE_CADENCES, RECURRING_KINDS, REMINDER_EVENTS, accentColorsStylesheet, accentCssVars, canvasConfigSchema, canvasSettingsSchema, contrastRatio, getPalette, isAccentColorId, isPaletteId, oklchToCss, oklchToHex, oklchToInt, oklchToRgb, paletteCssVars, palettesStylesheet, relativeLuminance, resolveAccentColor, slotColor, themeStylesheet, userPreferencesPatchSchema, userPreferencesSchema } from './chunk-CRYUOAYG.js';
import { z } from 'zod';

// src/money.ts
var MINOR_DIGITS = 2;
var SUFFIX_EXPONENT = {
  k: 3,
  m: 6,
  l: 5,
  lac: 5,
  lakh: 5,
  lakhs: 5,
  cr: 7,
  crore: 7,
  crores: 7
};
var CURRENCY_TOKENS = /₹|rs\.?|inr|\$|€|£/gi;
function isMinorUnits(value) {
  return typeof value === "number" && Number.isSafeInteger(value);
}
function assertMinorUnits(value, label = "amount") {
  if (!isMinorUnits(value)) {
    throw new TypeError(`${label} must be a safe integer in minor units, got ${String(value)}`);
  }
  return value;
}
function parseMoney(input) {
  if (input == null) return null;
  if (typeof input === "number") {
    if (!Number.isFinite(input)) return null;
    return parseMoney(input.toFixed(6));
  }
  let s = input.trim().toLowerCase().replace(CURRENCY_TOKENS, "").replace(/[\s,_']/g, "");
  if (s === "") return null;
  let negative = false;
  if (s.startsWith("(") && s.endsWith(")")) {
    negative = true;
    s = s.slice(1, -1);
  }
  if (s.startsWith("-")) {
    negative = !negative;
    s = s.slice(1);
  } else if (s.startsWith("+")) {
    s = s.slice(1);
  }
  let exponent = MINOR_DIGITS;
  const suffix = /([a-z]+)$/.exec(s)?.[1];
  if (suffix) {
    const e = SUFFIX_EXPONENT[suffix];
    if (e === void 0) return null;
    exponent += e;
    s = s.slice(0, -suffix.length);
  }
  const match = /^(\d*)(?:\.(\d*))?$/.exec(s);
  if (!match) return null;
  const intPart = match[1] ?? "";
  const fracPart = match[2] ?? "";
  if (intPart === "" && fracPart === "") return null;
  const digits = intPart + fracPart;
  const shift = exponent - fracPart.length;
  let minor;
  if (shift >= 0) {
    minor = BigInt(digits || "0") * 10n ** BigInt(shift);
  } else {
    const keep = digits.slice(0, shift) || "0";
    const dropped = digits.slice(shift);
    minor = BigInt(keep) + (dropped.charCodeAt(0) >= 53 ? 1n : 0n);
  }
  if (negative) minor = -minor;
  if (minor > BigInt(Number.MAX_SAFE_INTEGER) || minor < BigInt(Number.MIN_SAFE_INTEGER)) {
    return null;
  }
  return Number(minor);
}
function toMinor(major) {
  const parsed = parseMoney(major);
  if (parsed === null) throw new TypeError(`cannot convert ${String(major)} to minor units`);
  return parsed;
}
function toMajor(minor) {
  return assertMinorUnits(minor) / 10 ** MINOR_DIGITS;
}
var PRIVACY_MASK_TEXT = "\u2022\u2022\u2022";
var formatterCache = /* @__PURE__ */ new Map();
function numberFormat(locale, currency, signDisplay, fraction) {
  const key = `${locale}|${currency}|${signDisplay}|${fraction}`;
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: fraction,
      maximumFractionDigits: fraction,
      signDisplay
    });
    formatterCache.set(key, f);
  }
  return f;
}
function formatMoney(minor, options = {}) {
  assertMinorUnits(minor);
  const {
    locale = "en-IN",
    currency = "INR",
    signDisplay = "auto",
    trimZeroFraction = false
  } = options;
  const fraction = trimZeroFraction && minor % 100 === 0 ? 0 : MINOR_DIGITS;
  return numberFormat(locale, currency, signDisplay, fraction).format(minor / 10 ** MINOR_DIGITS);
}
function currencySymbol(locale = "en-IN", currency = "INR") {
  const parts = numberFormat(locale, currency, "auto", 0).formatToParts(0);
  return parts.find((p) => p.type === "currency")?.value ?? currency;
}
function formatMoneyCompact(minor, options = {}) {
  assertMinorUnits(minor);
  const { locale = "en-IN", currency = "INR" } = options;
  const abs = Math.abs(minor);
  const sign = minor < 0 ? "-" : "";
  const symbol = currencySymbol(locale, currency);
  const scaled = (divisor, digits, suffix) => {
    const value = (abs / divisor).toFixed(digits).replace(/\.?0+$/, "");
    return `${sign}${symbol}${value}${suffix}`;
  };
  if (abs >= 1e7 * 100) return scaled(1e7 * 100, 2, "Cr");
  if (abs >= 1e5 * 100) return scaled(1e5 * 100, 2, "L");
  if (abs >= 1e3 * 100) return scaled(1e3 * 100, 1, "K");
  return formatMoney(minor, { locale, currency, trimZeroFraction: true });
}
function sumMinor(values) {
  let total = 0;
  for (const v of values) total += assertMinorUnits(v);
  return total;
}
function ratio(part, whole) {
  assertMinorUnits(part);
  assertMinorUnits(whole);
  return whole === 0 ? 0 : part / whole;
}
function pctOf(minor, pct) {
  assertMinorUnits(minor);
  return Math.round(minor * pct / 100);
}

// src/period.ts
var PERIOD_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;
var ISO_DATE_RE = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
function isPeriod(value) {
  return typeof value === "string" && PERIOD_RE.test(value);
}
function parsePeriod(period) {
  const m = PERIOD_RE.exec(period);
  if (!m) throw new RangeError(`invalid period: ${period}`);
  return { year: Number(m[1]), month: Number(m[2]) };
}
function toPeriod(year, month) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    throw new RangeError(`invalid year/month: ${year}/${month}`);
  }
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
}
function addMonths(period, n) {
  const { year, month } = parsePeriod(period);
  const index = year * 12 + (month - 1) + n;
  return toPeriod(Math.floor(index / 12), index % 12 + 1);
}
var nextPeriod = (period) => addMonths(period, 1);
var prevPeriod = (period) => addMonths(period, -1);
function diffMonths(from, to) {
  const a = parsePeriod(from);
  const b = parsePeriod(to);
  return (b.year - a.year) * 12 + (b.month - a.month);
}
function comparePeriods(a, b) {
  parsePeriod(a);
  parsePeriod(b);
  return a < b ? -1 : a > b ? 1 : 0;
}
function isPeriodInRange(period, from, to) {
  return comparePeriods(period, from) >= 0 && (to === void 0 || comparePeriods(period, to) <= 0);
}
function monthOfPeriod(period) {
  return parsePeriod(period).month;
}
function periodFromIsoDate(isoDate) {
  if (!ISO_DATE_RE.test(isoDate)) throw new RangeError(`invalid ISO date: ${isoDate}`);
  return isoDate.slice(0, 7);
}
function dayOfIsoDate(isoDate) {
  if (!ISO_DATE_RE.test(isoDate)) throw new RangeError(`invalid ISO date: ${isoDate}`);
  return Number(isoDate.slice(8, 10));
}
function addMonthsToIsoDate(isoDate, n) {
  if (!ISO_DATE_RE.test(isoDate)) throw new RangeError(`invalid ISO date: ${isoDate}`);
  const year = Number(isoDate.slice(0, 4));
  const month = Number(isoDate.slice(5, 7));
  const day = Number(isoDate.slice(8, 10));
  const index = year * 12 + (month - 1) + n;
  const targetYear = Math.floor(index / 12);
  const targetMonth = index % 12 + 1;
  const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate();
  const clampedDay = Math.min(day, lastDayOfTargetMonth);
  return `${String(targetYear).padStart(4, "0")}-${String(targetMonth).padStart(2, "0")}-${String(clampedDay).padStart(2, "0")}`;
}
function addDaysToIsoDate(isoDate, n) {
  if (!ISO_DATE_RE.test(isoDate)) throw new RangeError(`invalid ISO date: ${isoDate}`);
  const year = Number(isoDate.slice(0, 4));
  const month = Number(isoDate.slice(5, 7));
  const day = Number(isoDate.slice(8, 10));
  const d = new Date(Date.UTC(year, month - 1, day + n));
  return `${String(d.getUTCFullYear()).padStart(4, "0")}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}
function dateInPeriod(period, dayOfMonth) {
  const { year, month } = parsePeriod(period);
  const lastDayOfMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const clampedDay = Math.min(dayOfMonth, lastDayOfMonth);
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(clampedDay).padStart(2, "0")}`;
}
var isoDateFormatters = /* @__PURE__ */ new Map();
function isoDateInZone(instant, timeZone) {
  let f = isoDateFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
    isoDateFormatters.set(timeZone, f);
  }
  const parts = f.formatToParts(instant);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}
function periodFromDate(instant, timeZone) {
  return periodFromIsoDate(isoDateInZone(instant, timeZone));
}
function periodLabel(period, locale = "en-IN", month = "long") {
  const { year, month: m } = parsePeriod(period);
  return new Intl.DateTimeFormat(locale, { month, year: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(year, m - 1, 1))
  );
}
function periodRange(from, to) {
  const n = diffMonths(from, to);
  if (n < 0) return [];
  return Array.from({ length: n + 1 }, (_, i) => addMonths(from, i));
}
var idSchema = z.string().regex(/^[0-9a-f]{24}$/, "expected a 24-character hex id");
var periodSchema = z.string().regex(PERIOD_RE, "expected a period like 2026-10");
var isoDateSchema = z.string().regex(ISO_DATE_RE, "expected a date like 2026-10-18");
var instantSchema = z.iso.datetime();
var minorUnitsSchema = z.number().int("amounts are integers in minor units").safe("amount out of range");
var nonNegativeMinorUnitsSchema = minorUnitsSchema.min(0);
var pctSchema = z.number().min(0).max(100).refine((v) => Math.round(v * 10) === v * 10, "at most one decimal place");
var slugSchema = z.string().regex(/^[a-z][a-z0-9_-]{1,31}$/, "lower-case letters, digits, _ or -, 2\u201332 chars");
var bucketDefSchema = z.object({
  key: slugSchema,
  name: z.string().trim().min(1).max(40),
  targetPct: pctSchema,
  colorIndex: z.number().int().min(0).max(5),
  colorOverride: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a six-digit hex color").nullable().optional(),
  absorbsSpillOver: z.boolean(),
  sortOrder: z.number().int().min(0)
});
var PCT_TOLERANCE = 1e-3;
var bucketsSchema = z.array(bucketDefSchema).min(2, "at least two buckets").max(8, "at most eight buckets").superRefine((buckets, ctx) => {
  const keys = /* @__PURE__ */ new Set();
  buckets.forEach((b, i) => {
    if (keys.has(b.key)) {
      ctx.addIssue({
        code: "custom",
        path: [i, "key"],
        message: `duplicate bucket key "${b.key}"`
      });
    }
    keys.add(b.key);
  });
  const total = buckets.reduce((s, b) => s + b.targetPct, 0);
  if (Math.abs(total - 100) > PCT_TOLERANCE) {
    ctx.addIssue({ code: "custom", message: `bucket targets must sum to 100 (got ${total})` });
  }
  const absorbing = buckets.filter((b) => b.absorbsSpillOver).length;
  if (absorbing !== 1) {
    ctx.addIssue({
      code: "custom",
      message: `exactly one bucket must absorb spill-over (got ${absorbing})`
    });
  }
});
function spillOverBucket(buckets) {
  const b = buckets.find((x) => x.absorbsSpillOver);
  if (!b) throw new Error("no bucket absorbs spill-over");
  return b;
}
var memberRoleSchema = z.enum(MEMBER_ROLES);
var personRoleSchema = z.enum(PERSON_ROLES);
var currencySchema = z.string().trim().length(3, "expected a 3-letter currency code, e.g. INR").transform((v) => v.toUpperCase());
var colorIndexSchema = z.number().int().min(0).max(5);
var personSchema = z.object({
  name: z.string().trim().min(1).max(60),
  userId: idSchema.optional(),
  role: personRoleSchema.default("adult"),
  colorIndex: colorIndexSchema.optional()
});
var personPatchSchema = personSchema.partial();
var householdSettingsSchema = z.object({
  monthOpenDay: z.number().int().min(1).max(28),
  settleByDay: z.number().int().min(1).max(28),
  safeAccessAccountId: idSchema.optional()
});
var householdSettingsPatchSchema = householdSettingsSchema.partial();
var STARTER_TEMPLATES = ["indian-50-30-20", "blank"];
var createHouseholdBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  currency: currencySchema.optional(),
  timezone: z.string().trim().min(1).optional(),
  // Only chooses the household's starting bucket set today — materialising an actual starter
  // budget template is P1-04, once the template module exists.
  starterTemplate: z.enum(STARTER_TEMPLATES).default("indian-50-30-20")
});
var patchHouseholdBodySchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  currency: currencySchema.optional(),
  locale: z.string().trim().min(1).optional(),
  timezone: z.string().trim().min(1).optional(),
  settings: householdSettingsPatchSchema.optional()
});
var putBucketsBodySchema = z.object({
  buckets: bucketsSchema,
  reassignments: z.record(slugSchema, slugSchema).optional()
});
var createInviteBodySchema = z.object({
  personId: idSchema.optional(),
  email: z.email().transform((v) => v.toLowerCase()),
  role: memberRoleSchema.default("member")
});
var transferOwnershipBodySchema = z.object({
  toUserId: idSchema
});
var accountTypeSchema = z.enum(ACCOUNT_TYPES);
var dayOfMonthSchema = z.number().int().min(1).max(31);
var importMappingSchema = z.object({
  date: z.string().optional(),
  postingDate: z.string().optional(),
  title: z.string().min(1),
  amount: z.string().optional(),
  debit: z.string().optional(),
  credit: z.string().optional(),
  dateFormat: z.string().optional(),
  amountSign: z.enum(["debit_positive", "debit_negative"]),
  skipRows: z.number().int().min(0).optional()
});
var accountBankingSchema = z.object({
  kind: z.enum(["savings", "salary", "current", "nre", "nro"]).default("savings"),
  ifsc: z.string().trim().toUpperCase().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Enter an 11-character IFSC").optional(),
  branch: z.string().trim().max(100).optional(),
  balance: nonNegativeMinorUnitsSchema.optional(),
  balanceAsOf: isoDateSchema.optional()
});
var accountCardSchema = z.object({
  network: z.enum(["visa", "mastercard", "rupay", "amex", "diners", "other"]).optional(),
  productName: z.string().trim().max(80).optional(),
  annualFee: nonNegativeMinorUnitsSchema.optional(),
  statementDay: dayOfMonthSchema.optional(),
  dueDay: dayOfMonthSchema.optional(),
  creditLimit: nonNegativeMinorUnitsSchema.optional(),
  importMapping: importMappingSchema.optional()
});
function requireCardOnlyForCreditCard(value, ctx) {
  if (value.banking !== void 0 && value.type !== void 0 && value.type !== "bank") {
    ctx.addIssue({
      code: "custom",
      path: ["banking"],
      message: "only bank accounts may have banking details"
    });
  }
  if (value.card !== void 0 && value.type !== void 0 && value.type !== "credit_card") {
    ctx.addIssue({
      code: "custom",
      path: ["card"],
      message: "only credit_card accounts may have card details"
    });
  }
}
var accountSchema = z.object({
  _id: idSchema,
  householdId: idSchema,
  name: z.string().trim().min(1).max(80),
  type: accountTypeSchema,
  bank: z.string().trim().min(1).max(120).optional(),
  banking: accountBankingSchema.optional(),
  last4: z.string().regex(/^\d{4}$/, "last4 must be exactly 4 digits").optional(),
  ownerPersonId: idSchema.optional(),
  isActive: z.boolean(),
  colorIndex: colorIndexSchema.optional(),
  sortOrder: z.number().int().min(0),
  card: accountCardSchema.optional()
}).superRefine(requireCardOnlyForCreditCard);
var createAccountBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  type: accountTypeSchema,
  bank: z.string().trim().min(1).max(120).optional(),
  banking: accountBankingSchema.optional(),
  last4: z.string().regex(/^\d{4}$/, "last4 must be exactly 4 digits").optional(),
  ownerPersonId: idSchema.optional(),
  colorIndex: colorIndexSchema.optional(),
  card: z.object({
    network: z.enum(["visa", "mastercard", "rupay", "amex", "diners", "other"]).optional(),
    productName: z.string().trim().max(80).optional(),
    annualFee: nonNegativeMinorUnitsSchema.optional(),
    statementDay: dayOfMonthSchema.optional(),
    dueDay: dayOfMonthSchema.optional(),
    creditLimit: nonNegativeMinorUnitsSchema.optional()
  }).optional()
}).superRefine(requireCardOnlyForCreditCard);
var patchAccountBodySchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  type: accountTypeSchema.optional(),
  bank: z.string().trim().min(1).max(120).optional(),
  banking: accountBankingSchema.optional(),
  last4: z.string().regex(/^\d{4}$/, "last4 must be exactly 4 digits").nullable().optional(),
  ownerPersonId: idSchema.optional(),
  colorIndex: colorIndexSchema.optional(),
  card: z.object({
    network: z.enum(["visa", "mastercard", "rupay", "amex", "diners", "other"]).optional(),
    productName: z.string().trim().max(80).optional(),
    annualFee: nonNegativeMinorUnitsSchema.optional(),
    statementDay: dayOfMonthSchema.optional(),
    dueDay: dayOfMonthSchema.optional(),
    creditLimit: nonNegativeMinorUnitsSchema.optional()
  }).optional(),
  isActive: z.boolean().optional()
}).superRefine(requireCardOnlyForCreditCard);
function requireBucketKeyForExpense(value, ctx) {
  if (value.kind === "expense" && !value.bucketKey) {
    ctx.addIssue({ code: "custom", path: ["bucketKey"], message: "required for expense lines" });
  }
  if (value.kind === "income" && value.bucketKey) {
    ctx.addIssue({
      code: "custom",
      path: ["bucketKey"],
      message: "must be absent for income lines"
    });
  }
}
var classificationSchema = z.enum(CLASSIFICATIONS);
var lineSourceSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("manual") }),
  z.object({
    type: z.literal("recurring"),
    kinds: z.array(z.enum(RECURRING_KINDS)).optional(),
    cadence: z.array(z.enum(CADENCES)).optional(),
    classification: classificationSchema.optional()
  }),
  z.object({
    type: z.literal("credit_card"),
    cardAccountId: idSchema.optional(),
    classification: classificationSchema.optional()
  }),
  z.object({ type: z.literal("carry_over") })
]);
var prepaymentModeSchema = z.enum(LOAN_PREPAYMENT_MODES);
var lineLinkSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("loan"),
    loanId: idSchema,
    kind: z.enum(["emi", "prepayment"]),
    mode: prepaymentModeSchema.optional()
  }),
  z.object({ type: z.literal("deposit"), depositId: idSchema }),
  z.object({ type: z.literal("investment"), investmentId: idSchema }),
  z.object({ type: z.literal("goal"), goalId: idSchema }),
  z.object({ type: z.literal("hand_loan"), handLoanId: idSchema })
]);
var recurrenceSchema = z.discriminatedUnion("cadence", [
  z.object({ cadence: z.literal("monthly") }),
  z.object({ cadence: z.literal("on_demand") }),
  z.object({ cadence: z.literal("quarterly"), anchorPeriod: periodSchema }),
  z.object({ cadence: z.literal("half_yearly"), anchorPeriod: periodSchema }),
  z.object({ cadence: z.literal("yearly"), anchorPeriod: periodSchema }),
  z.object({
    cadence: z.literal("custom"),
    months: z.array(z.number().int().min(1).max(12)).min(1)
  })
]);
var templateLineFieldsSchema = z.object({
  kind: z.enum(LINE_KINDS),
  bucketKey: slugSchema.optional(),
  section: z.string().trim().min(1).max(60),
  subsection: z.string().trim().min(1).max(60).optional(),
  name: z.string().trim().min(1).max(120),
  personId: idSchema.optional(),
  accountId: idSchema.optional(),
  plannedAmount: minorUnitsSchema,
  source: lineSourceSchema,
  link: lineLinkSchema.optional(),
  recurrence: recurrenceSchema,
  dueRule: z.enum(["day", "none", "last_working_day"]).optional(),
  dueDay: z.number().int().min(1).max(31).optional(),
  reminderDaysBefore: z.array(z.number().int().min(0)).optional(),
  sortOrder: z.number().int().min(0).optional()
});
var templateLineSchema = z.object({
  _id: idSchema,
  householdId: idSchema,
  effectiveFrom: periodSchema,
  effectiveTo: periodSchema.optional(),
  supersedes: idSchema.optional()
}).extend(templateLineFieldsSchema.shape).extend({ sortOrder: z.number().int().min(0) }).superRefine(requireBucketKeyForExpense);
var createTemplateLineBodySchema = templateLineFieldsSchema.extend({ effectiveFrom: periodSchema }).superRefine(requireBucketKeyForExpense);
var patchTemplateLineBodySchema = z.object({
  changes: templateLineFieldsSchema.partial().superRefine(requireBucketKeyForExpense),
  fromPeriod: periodSchema
});
var editScopeSchema = z.enum(EDIT_SCOPES);
var monthLineComputedSchema = z.object({
  plannedAmount: minorUnitsSchema,
  actualAmount: minorUnitsSchema,
  detail: z.unknown(),
  computedAt: instantSchema
});
var monthLineSchema = z.object({
  _id: idSchema,
  templateLineId: idSchema.optional(),
  kind: z.enum(LINE_KINDS),
  bucketKey: slugSchema.optional(),
  section: z.string().trim().min(1).max(60),
  subsection: z.string().trim().min(1).max(60).optional(),
  name: z.string().trim().min(1).max(120),
  personId: idSchema.optional(),
  accountId: idSchema.optional(),
  plannedAmount: minorUnitsSchema,
  actualAmount: minorUnitsSchema.optional(),
  status: z.enum(LINE_STATUSES),
  paidAt: instantSchema.optional(),
  paidBy: idSchema.optional(),
  note: z.string().trim().max(500).optional(),
  source: lineSourceSchema,
  link: lineLinkSchema.optional(),
  dueDay: z.number().int().min(1).max(31).optional(),
  reminderDaysBefore: z.array(z.number().int().min(0)).optional(),
  overridden: z.boolean(),
  computed: monthLineComputedSchema.optional(),
  sortOrder: z.number().int().min(0)
}).superRefine(requireBucketKeyForExpense);
var monthSummarySchema = z.looseObject({
  buckets: bucketsSchema,
  closedBy: idSchema
});
var monthSchema = z.object({
  _id: idSchema,
  householdId: idSchema,
  period: periodSchema,
  status: z.enum(MONTH_STATUSES),
  openedAt: instantSchema,
  setupRequired: z.boolean().default(false),
  setupCompletedAt: instantSchema.optional(),
  closedAt: instantSchema.optional(),
  carryOverIn: minorUnitsSchema,
  lines: z.array(monthLineSchema).max(300),
  summary: monthSummarySchema.optional()
});
var addMonthLineBodySchema = z.object({
  kind: z.enum(LINE_KINDS),
  bucketKey: slugSchema.optional(),
  section: z.string().trim().min(1).max(60),
  subsection: z.string().trim().min(1).max(60).optional(),
  name: z.string().trim().min(1).max(120),
  personId: idSchema.optional(),
  accountId: idSchema.optional(),
  plannedAmount: minorUnitsSchema,
  dueDay: z.number().int().min(1).max(31).optional(),
  reminderDaysBefore: z.array(z.number().int().min(0)).optional(),
  addToTemplate: z.boolean().default(false)
}).superRefine(requireBucketKeyForExpense);
var patchMonthLineBodySchema = z.object({
  changes: z.object({
    bucketKey: slugSchema.optional(),
    section: z.string().trim().min(1).max(60).optional(),
    subsection: z.string().trim().min(1).max(60).optional(),
    name: z.string().trim().min(1).max(120).optional(),
    personId: idSchema.optional(),
    accountId: idSchema.optional(),
    plannedAmount: minorUnitsSchema.optional(),
    note: z.string().trim().max(500).optional(),
    dueDay: z.number().int().min(1).max(31).optional(),
    reminderDaysBefore: z.array(z.number().int().min(0)).optional()
  }),
  scope: editScopeSchema
});
var setMonthLineStatusBodySchema = z.object({
  status: z.enum(LINE_STATUSES),
  actualAmount: minorUnitsSchema.optional()
});
var recurringKindSchema = z.enum(RECURRING_KINDS);
var recurringCadenceSchema = z.enum(CADENCES);
var classificationSchema2 = z.enum(CLASSIFICATIONS);
var RECURRING_STATUSES = ["active", "paused", "cancelled", "completed"];
var recurringStatusSchema = z.enum(RECURRING_STATUSES);
function requireNextDueDateUnlessMonthly(value, ctx) {
  if (value.cadence !== void 0 && value.cadence !== "monthly" && !value.nextDueDate) {
    ctx.addIssue({
      code: "custom",
      path: ["nextDueDate"],
      message: "required unless cadence is monthly"
    });
  }
}
var recurringDetailsSchema = z.object({
  provider: z.string().trim().max(120).optional(),
  reference: z.string().trim().max(120).optional(),
  plan: z.string().trim().max(120).optional(),
  insuranceType: z.enum(["health", "life", "vehicle", "home", "travel", "other"]).optional(),
  insuredPerson: z.string().trim().max(120).optional(),
  coverageAmount: minorUnitsSchema.optional(),
  policyEndDate: isoDateSchema.optional()
});
var recurringFieldsSchema = z.object({
  details: recurringDetailsSchema.optional(),
  name: z.string().trim().min(1).max(120),
  kind: recurringKindSchema,
  amount: minorUnitsSchema.min(1),
  cadence: recurringCadenceSchema,
  classification: classificationSchema2,
  bucketKey: slugSchema,
  accountId: idSchema.nullable().optional(),
  autoDebit: z.boolean().optional(),
  trackInMonth: z.boolean().optional(),
  personId: idSchema.optional(),
  nextDueDate: isoDateSchema.optional(),
  // No `.default()` here — the default depends on `kind` (docs/02 §2.7: "default subscription
  // [3,0]; insurance/tax [30,7,0]"), which `createRecurring` (recurring.service.ts) applies.
  reminderDaysBefore: z.array(z.number().int().min(0)).optional(),
  autoRenew: z.boolean().default(true),
  notes: z.string().trim().max(500).optional(),
  url: z.url().optional()
});
var recurringPaymentSchema = z.object({ _id: idSchema, householdId: idSchema, status: recurringStatusSchema }).extend(recurringFieldsSchema.shape).extend({ reminderDaysBefore: z.array(z.number().int().min(0)) }).superRefine(requireNextDueDateUnlessMonthly);
var createRecurringBodySchema = recurringFieldsSchema.superRefine(
  requireNextDueDateUnlessMonthly
);
var patchRecurringBodySchema = recurringFieldsSchema.partial().extend({ status: recurringStatusSchema.optional() }).superRefine(requireNextDueDateUnlessMonthly);
var markRecurringPaidBodySchema = z.object({ monthLineId: idSchema.optional(), period: periodSchema.optional() }).refine((v) => !v.monthLineId || v.period !== void 0, {
  message: "period is required when monthLineId is given",
  path: ["period"]
});
var creditEmiTermsSchema = z.object({
  annualRatePct: z.number().min(0).max(60),
  processingFee: z.number().int().min(0).max(1e8),
  gstRatePct: z.number().min(0).max(30).default(18),
  taxMode: z.enum(["split", "igst"]),
  offer: z.enum(["standard", "upfront_discount", "cashback"]),
  purchaseAmount: z.number().int().positive().max(1e10),
  benefit: z.number().int().min(0).max(1e10)
});
function creditEmiSchedule(principal, months, terms) {
  if (!Number.isSafeInteger(principal) || principal <= 0 || !Number.isInteger(months) || months < 1 || months > 120)
    throw new Error("Invalid EMI principal or tenure");
  const r = terms.annualRatePct / 1200;
  const base = Math.round(r ? principal * r / (1 - (1 + r) ** -months) : principal / months);
  let balance = principal;
  return Array.from({ length: months }, (_, index) => {
    const interest = Math.round(balance * r);
    const principalPaid = index === months - 1 ? balance : Math.min(balance, Math.max(0, base - interest));
    balance -= principalPaid;
    const fee = index === 0 ? terms.processingFee : 0;
    const taxable = interest + fee;
    const cgst = terms.taxMode === "split" ? Math.round(taxable * terms.gstRatePct / 200) : 0;
    const sgst = cgst;
    const igst = terms.taxMode === "igst" ? Math.round(taxable * terms.gstRatePct / 100) : 0;
    return {
      installment: index + 1,
      principal: principalPaid,
      interest,
      fee,
      cgst,
      sgst,
      igst,
      balance,
      total: principalPaid + interest + fee + cgst + sgst + igst
    };
  });
}
function creditEmiDue(emi2, period) {
  const index = diffMonths(emi2.startPeriod, period);
  if (index < 0 || index >= emi2.tenureMonths) return 0;
  if (!emi2.creditTerms) return emi2.monthlyAmount;
  return creditEmiSchedule(emi2.originalAmount, emi2.tenureMonths, emi2.creditTerms)[index]?.total ?? 0;
}
var classificationSchema3 = z.enum(CLASSIFICATIONS);
var EMI_STATUSES = ["active", "closed"];
var emiStatusSchema = z.enum(EMI_STATUSES);
var emiFieldsSchema = z.object({
  creditTerms: creditEmiTermsSchema.optional(),
  cardAccountId: idSchema,
  description: z.string().trim().max(500).optional(),
  name: z.string().trim().min(1).max(120),
  originalAmount: minorUnitsSchema.min(1),
  monthlyAmount: minorUnitsSchema.min(1),
  tenureMonths: z.number().int().min(1).max(120),
  startPeriod: periodSchema,
  classification: classificationSchema3
});
var emiSchema = z.object({ _id: idSchema, householdId: idSchema, status: emiStatusSchema }).extend(emiFieldsSchema.shape);
var createEmiBodySchema = emiFieldsSchema;
var patchEmiBodySchema = emiFieldsSchema.omit({ cardAccountId: true }).partial().extend({ status: emiStatusSchema.optional() });
var TRANSACTION_CLASSIFICATIONS = [...CLASSIFICATIONS, "unclassified"];
var transactionClassificationSchema = z.enum(TRANSACTION_CLASSIFICATIONS);
var cardTransactionFieldsSchema = z.object({
  cardAccountId: idSchema,
  period: periodSchema,
  date: isoDateSchema.optional(),
  postingDate: isoDateSchema.optional(),
  postingStatus: z.enum(["posted", "pending"]).optional(),
  entryKind: z.enum(["purchase", "refund", "repayment", "other_credit"]).optional(),
  title: z.string().trim().min(1).max(200),
  amount: minorUnitsSchema,
  classification: transactionClassificationSchema.default("unclassified"),
  tag: z.string().trim().min(1).max(40).optional(),
  // Manual matching only for now — no import pipeline or classification-rule engine exists yet
  // (P2-04/P2-05) to suggest one automatically.
  emiId: idSchema.optional()
});
var cardTransactionSchema = z.object({
  _id: idSchema,
  householdId: idSchema,
  fingerprint: z.string(),
  importId: idSchema.optional(),
  suggestedBy: z.enum(["rule", "emi"]).optional()
}).extend(cardTransactionFieldsSchema.shape);
var createCardTransactionBodySchema = cardTransactionFieldsSchema.omit({
  cardAccountId: true,
  period: true
});
var createCardTransactionsBodySchema = z.union([
  createCardTransactionBodySchema,
  z.object({ items: z.array(createCardTransactionBodySchema).min(1).max(500) })
]);
var patchCardTransactionBodySchema = cardTransactionFieldsSchema.omit({ cardAccountId: true }).partial();
var bulkPatchCardTransactionRowSchema = z.object({
  id: idSchema,
  classification: transactionClassificationSchema.optional(),
  tag: z.string().trim().min(1).max(40).optional(),
  period: periodSchema.optional()
});
var bulkPatchCardTransactionsBodySchema = z.array(bulkPatchCardTransactionRowSchema).min(1).max(500);
var IMPORT_FILE_TYPES = ["csv", "xlsx", "pdf"];
var importFileTypeSchema = z.enum(IMPORT_FILE_TYPES);
var IMPORT_BANK_ADAPTERS = ["icici", "hdfc", "generic"];
var importBankAdapterSchema = z.enum(IMPORT_BANK_ADAPTERS);
var IMPORT_STATUSES = ["uploaded", "previewed", "committed", "discarded"];
var importStatusSchema = z.enum(IMPORT_STATUSES);
var importStatsSchema = z.object({
  total: z.number().int().min(0),
  imported: z.number().int().min(0),
  duplicates: z.number().int().min(0),
  emiMatched: z.number().int().min(0),
  suggested: z.number().int().min(0)
});
var cardImportSchema = z.object({
  _id: idSchema,
  householdId: idSchema,
  cardAccountId: idSchema,
  fileName: z.string().min(1),
  fileType: importFileTypeSchema,
  rowCount: z.number().int().min(0),
  bankAdapter: importBankAdapterSchema.optional(),
  columns: z.array(z.string()),
  sampleRows: z.array(z.array(z.string())),
  mapping: importMappingSchema.optional(),
  statementDate: isoDateSchema.optional(),
  targetPeriod: periodSchema.optional(),
  status: importStatusSchema,
  stats: importStatsSchema.optional(),
  rawStorageKey: z.string()
});
var uploadImportBodySchema = z.object({
  password: z.string().min(1).max(200).optional(),
  rememberPassword: z.union([z.boolean(), z.enum(["true", "false"])]).transform((v) => v === true || v === "true").optional()
});
var previewImportBodySchema = z.object({
  mapping: importMappingSchema,
  statementDate: isoDateSchema.optional(),
  targetPeriod: periodSchema.optional()
});
var commitImportRowSchema = z.object({
  idx: z.number().int().min(0),
  include: z.boolean(),
  classification: transactionClassificationSchema.optional(),
  tag: z.string().trim().min(1).max(40).optional()
});
var commitImportBodySchema = z.object({
  rows: z.array(commitImportRowSchema).min(1)
});
var classificationSchema4 = z.enum(CLASSIFICATIONS);
var CLASSIFICATION_RULE_SOURCES = ["learned", "manual"];
var classificationRuleSourceSchema = z.enum(CLASSIFICATION_RULE_SOURCES);
var classificationRuleSchema = z.object({
  _id: idSchema,
  householdId: idSchema,
  pattern: z.string().min(1),
  classification: classificationSchema4,
  tag: z.string().trim().min(1).max(40).optional(),
  hits: z.number().int().min(1),
  lastUsedAt: instantSchema,
  source: classificationRuleSourceSchema
});
var loanTypeSchema = z.enum(LOAN_TYPES);
var loanRateTypeSchema = z.enum(LOAN_RATE_TYPES);
var loanStatusSchema = z.enum(LOAN_STATUSES);
var loanPaymentKindSchema = z.enum(LOAN_PAYMENT_KINDS);
var rateResetFields = {
  repaymentMode: z.enum(["keep_emi", "set_emi", "recalculate_emi"]).optional(),
  revisedEmi: minorUnitsSchema.min(1).optional(),
  targetRemainingMonths: z.number().int().min(1).max(600).optional(),
  bankTenureMonths: z.number().int().min(1).max(600).optional(),
  bankTenureBasis: z.enum(["total", "remaining"]).optional()
};
var loanRateHistoryEntrySchema = z.object({
  effectiveDate: isoDateSchema,
  annualRatePct: z.number().min(0).max(100),
  note: z.string().trim().max(200).optional(),
  ...rateResetFields
});
function requireModeForPrepayment(value, ctx) {
  if (value.kind === "prepayment" && value.mode === void 0) {
    ctx.addIssue({ code: "custom", path: ["mode"], message: "required for a prepayment" });
  }
  if (value.kind === "emi" && value.mode !== void 0) {
    ctx.addIssue({ code: "custom", path: ["mode"], message: "must be absent for an emi payment" });
  }
}
var loanPaymentSchema = z.object({
  _id: idSchema,
  date: isoDateSchema,
  amount: minorUnitsSchema.min(1),
  kind: loanPaymentKindSchema,
  mode: prepaymentModeSchema.optional(),
  monthLineId: idSchema.optional(),
  note: z.string().trim().max(200).optional()
}).superRefine(requireModeForPrepayment);
var addLoanPaymentBodySchema = z.object({
  date: isoDateSchema,
  amount: minorUnitsSchema.min(1),
  kind: loanPaymentKindSchema,
  mode: prepaymentModeSchema.optional(),
  monthLineId: idSchema.optional(),
  note: z.string().trim().max(200).optional()
}).superRefine(requireModeForPrepayment);
var addLoanRateChangeBodySchema = loanRateHistoryEntrySchema.superRefine((value, ctx) => {
  if (value.repaymentMode === "set_emi" !== (value.revisedEmi !== void 0))
    ctx.addIssue({
      code: "custom",
      path: ["revisedEmi"],
      message: "Specify a revised EMI only with the bank-reported EMI option."
    });
  if (value.repaymentMode === "recalculate_emi" !== (value.targetRemainingMonths !== void 0))
    ctx.addIssue({
      code: "custom",
      path: ["targetRemainingMonths"],
      message: "Specify remaining months only when recalculating EMI."
    });
  if (value.bankTenureMonths !== void 0 !== (value.bankTenureBasis !== void 0))
    ctx.addIssue({
      code: "custom",
      path: ["bankTenureBasis"],
      message: "Identify whether the bank tenure is total or remaining."
    });
});
var emiTrackingFields = {
  emiTrackingEnabled: z.boolean().optional(),
  emiTrackingFrom: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional()
};
var loanFieldsSchema = z.object({
  emoji: z.string().trim().min(1).max(32).optional(),
  ...emiTrackingFields,
  recordFunding: z.boolean().optional(),
  previouslyPaidAmount: minorUnitsSchema.min(0).optional(),
  currentOutstanding: minorUnitsSchema.min(1).optional(),
  currentRemainingMonths: z.number().int().min(1).max(600).optional(),
  currentEmiAmount: minorUnitsSchema.min(1).optional(),
  balanceAsOf: isoDateSchema.optional(),
  additionalEmiAmount: minorUnitsSchema.min(0).optional(),
  description: z.string().trim().max(500).optional(),
  name: z.string().trim().min(1).max(120),
  lender: z.string().trim().min(1).max(120),
  loanAccountNumber: z.string().trim().min(4).max(40).regex(/^[A-Za-z0-9 /-]+$/, "Use letters, numbers, spaces, slashes or hyphens").optional(),
  type: loanTypeSchema,
  accountId: idSchema.optional(),
  personId: idSchema.optional(),
  principal: minorUnitsSchema.min(1),
  startDate: isoDateSchema,
  emiDay: z.number().int().min(1).max(31),
  tenureMonths: z.number().int().min(1).max(600),
  rateType: loanRateTypeSchema
});
var loanSchema = loanFieldsSchema.extend({
  _id: idSchema,
  householdId: idSchema,
  emiAmount: minorUnitsSchema.min(1),
  rateHistory: z.array(loanRateHistoryEntrySchema).min(1),
  payments: z.array(loanPaymentSchema),
  linkedEmiTemplateLineId: idSchema.optional(),
  linkedPrepaymentTemplateLineId: idSchema.optional(),
  status: loanStatusSchema
});
var createLoanBodySchema = loanFieldsSchema.extend({
  annualRatePct: z.number().min(0).max(100)
}).superRefine((value, ctx) => {
  const snapshot = [
    value.currentOutstanding,
    value.currentRemainingMonths,
    value.currentEmiAmount,
    value.balanceAsOf
  ];
  if (snapshot.some((v) => v !== void 0) && !snapshot.every((v) => v !== void 0))
    ctx.addIssue({
      code: "custom",
      path: ["currentOutstanding"],
      message: "Provide outstanding, remaining months, current EMI and first upcoming EMI date together."
    });
  if (value.balanceAsOf && (value.balanceAsOf < value.startDate || value.recordFunding !== false))
    ctx.addIssue({
      code: "custom",
      path: ["balanceAsOf"],
      message: "A current balance is for an ongoing loan, on or after its original start date."
    });
});
var patchLoanBodySchema = z.object({
  emoji: z.string().trim().min(1).max(32).optional(),
  ...emiTrackingFields,
  description: z.string().trim().max(500).optional(),
  name: z.string().trim().min(1).max(120).optional(),
  lender: z.string().trim().min(1).max(120).optional(),
  loanAccountNumber: z.string().trim().min(4).max(40).regex(/^[A-Za-z0-9 /-]+$/).optional(),
  type: loanTypeSchema.optional(),
  accountId: idSchema.optional(),
  personId: idSchema.optional(),
  emiDay: z.number().int().min(1).max(31).optional(),
  rateType: loanRateTypeSchema.optional(),
  status: loanStatusSchema.optional(),
  linkedEmiTemplateLineId: idSchema.optional(),
  linkedPrepaymentTemplateLineId: idSchema.optional()
});
var loanScenarioSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("extraMonthly"), amount: minorUnitsSchema.min(1) }),
  z.object({
    type: z.literal("lumpSum"),
    amount: minorUnitsSchema.min(1),
    date: isoDateSchema,
    mode: prepaymentModeSchema
  }),
  z.object({ type: z.literal("newEmi"), amount: minorUnitsSchema.min(1) }),
  z.object({
    type: z.literal("rateChange"),
    annualRatePct: z.number().min(0).max(100),
    from: isoDateSchema
  })
]);
var simulateLoanBodySchema = z.object({ scenario: loanScenarioSchema });
var depositTypeSchema = z.enum(DEPOSIT_TYPES);
var depositStatusSchema = z.enum(DEPOSIT_STATUSES);
var depositContributionSchema = z.object({
  _id: idSchema,
  date: isoDateSchema,
  amount: minorUnitsSchema.min(1),
  monthLineId: idSchema.optional(),
  note: z.string().trim().max(200).optional()
});
var addDepositContributionBodySchema = z.object({
  date: isoDateSchema,
  amount: minorUnitsSchema.min(1),
  monthLineId: idSchema.optional(),
  note: z.string().trim().max(200).optional()
});
function requirePrincipalOrInstallmentByType(value, ctx) {
  if (!value.maturityDateOverride && (value.tenureMonths ?? 0) === 0 && (value.tenureDays ?? 0) === 0)
    ctx.addIssue({ code: "custom", path: ["tenureMonths"], message: "Enter a positive tenure" });
  if (value.type === "fd") {
    if (value.principal === void 0) {
      ctx.addIssue({ code: "custom", path: ["principal"], message: "required for an FD" });
    }
    if (value.installment !== void 0) {
      ctx.addIssue({ code: "custom", path: ["installment"], message: "must be absent for an FD" });
    }
  }
  if (value.type === "rd") {
    if (value.installment === void 0) {
      ctx.addIssue({ code: "custom", path: ["installment"], message: "required for an RD" });
    }
    if (value.principal !== void 0) {
      ctx.addIssue({ code: "custom", path: ["principal"], message: "must be absent for an RD" });
    }
  }
}
var depositFieldsSchema = z.object({
  maturityDateOverride: isoDateSchema.nullable().optional(),
  maturityAmountOverride: minorUnitsSchema.min(1).nullable().optional(),
  type: depositTypeSchema,
  name: z.string().trim().min(1).max(120),
  accountId: idSchema.optional(),
  ownerPersonId: idSchema.optional(),
  ownerPersonIds: z.array(idSchema).max(50).refine((ids) => new Set(ids).size === ids.length, "Owners must be unique").optional(),
  bank: z.string().trim().max(120).optional(),
  accountNumber: z.string().trim().max(120).optional(),
  reference: z.string().trim().max(60).optional(),
  principal: minorUnitsSchema.min(1).optional(),
  installment: minorUnitsSchema.min(1).optional(),
  interestRatePct: z.number().min(0).max(100).optional(),
  startDate: isoDateSchema,
  tenureMonths: z.number().int().min(0).max(600),
  tenureDays: z.number().int().min(0).max(18300).optional(),
  purpose: z.string().trim().max(200).optional(),
  goalId: idSchema.optional(),
  autoRenew: z.boolean().default(false)
});
var depositSchema = depositFieldsSchema.extend({
  _id: idSchema,
  householdId: idSchema,
  maturityDate: isoDateSchema.optional(),
  maturityAmount: minorUnitsSchema.optional(),
  receiptDate: isoDateSchema.optional(),
  receiptAmount: minorUnitsSchema.min(1).optional(),
  linkedTemplateLineId: idSchema.optional(),
  contributions: z.array(depositContributionSchema),
  status: depositStatusSchema
}).superRefine(requirePrincipalOrInstallmentByType);
var createDepositBodySchema = depositFieldsSchema.extend({ createMonthlyPlan: z.boolean().optional() }).superRefine(requirePrincipalOrInstallmentByType);
var patchDepositBodySchema = z.object({
  startDate: isoDateSchema.optional(),
  tenureMonths: z.number().int().min(0).max(600).optional(),
  tenureDays: z.number().int().min(0).max(18300).optional(),
  maturityDateOverride: isoDateSchema.nullable().optional(),
  maturityAmountOverride: minorUnitsSchema.min(1).nullable().optional(),
  name: z.string().trim().min(1).max(120).optional(),
  accountId: idSchema.nullable().optional(),
  ownerPersonId: idSchema.optional(),
  ownerPersonIds: z.array(idSchema).max(50).refine((ids) => new Set(ids).size === ids.length, "Owners must be unique").optional(),
  bank: z.string().trim().max(120).optional(),
  accountNumber: z.string().trim().max(120).optional(),
  reference: z.string().trim().max(60).optional(),
  interestRatePct: z.number().min(0).max(100).optional(),
  purpose: z.string().trim().max(200).optional(),
  goalId: idSchema.optional(),
  autoRenew: z.boolean().optional(),
  linkedTemplateLineId: idSchema.optional(),
  status: depositStatusSchema.optional()
});
var handLoanDirectionSchema = z.enum(HAND_LOAN_DIRECTIONS);
var handLoanStatusSchema = z.enum(HAND_LOAN_STATUSES);
var handLoanRepaymentSchema = z.object({
  _id: idSchema,
  date: isoDateSchema,
  amount: minorUnitsSchema.min(1),
  monthLineId: idSchema.optional(),
  note: z.string().trim().max(200).optional()
});
var addHandLoanRepaymentBodySchema = z.object({
  date: isoDateSchema,
  amount: minorUnitsSchema.min(1),
  monthLineId: idSchema.optional(),
  note: z.string().trim().max(200).optional()
});
var handLoanFieldsSchema = z.object({
  emoji: z.string().trim().min(1).max(32).optional(),
  direction: handLoanDirectionSchema,
  title: z.string().trim().max(120).optional(),
  counterparty: z.string().trim().min(1).max(120),
  personId: idSchema.optional(),
  accountId: idSchema.optional(),
  monthlyRepayment: minorUnitsSchema.min(1).optional(),
  repaymentDay: z.number().int().min(1).max(31).optional(),
  amount: minorUnitsSchema.min(1),
  date: isoDateSchema,
  dueDate: isoDateSchema.optional(),
  interestRatePct: z.number().min(0).max(100).optional(),
  notes: z.string().trim().max(500).optional(),
  // No `.default()` here — the default is applied by `handLoans.service.ts#createHandLoan` when
  // omitted, same reasoning as `recurring.ts`'s kind-conditional `reminderDaysBefore` default
  // (there's no `kind` to condition on here, just a flat default).
  reminderDaysBefore: z.array(z.number().int().min(0)).optional()
});
var handLoanSchema = handLoanFieldsSchema.extend({
  _id: idSchema,
  householdId: idSchema,
  linkedTemplateLineId: idSchema.optional(),
  repayments: z.array(handLoanRepaymentSchema),
  status: handLoanStatusSchema,
  reminderDaysBefore: z.array(z.number().int().min(0))
});
var createHandLoanBodySchema = handLoanFieldsSchema;
var patchHandLoanBodySchema = z.object({
  emoji: z.string().trim().min(1).max(32).optional(),
  title: z.string().trim().max(120).optional(),
  counterparty: z.string().trim().min(1).max(120).optional(),
  personId: idSchema.optional(),
  accountId: idSchema.optional(),
  monthlyRepayment: minorUnitsSchema.min(1).optional(),
  repaymentDay: z.number().int().min(1).max(31).optional(),
  dueDate: isoDateSchema.optional(),
  interestRatePct: z.number().min(0).max(100).optional(),
  notes: z.string().trim().max(500).optional(),
  reminderDaysBefore: z.array(z.number().int().min(0)).optional(),
  linkedTemplateLineId: idSchema.optional(),
  status: handLoanStatusSchema.optional()
});
var goalStatusSchema = z.enum(GOAL_STATUSES);
var goalContributionSourceSchema = z.enum(["manual", "line", "deposit"]);
var goalContributionSchema = z.object({
  _id: idSchema,
  date: isoDateSchema,
  amount: minorUnitsSchema.min(1),
  source: goalContributionSourceSchema,
  refId: idSchema.optional(),
  note: z.string().trim().max(200).optional()
});
var addGoalContributionBodySchema = z.object({
  date: isoDateSchema,
  amount: minorUnitsSchema.min(1),
  note: z.string().trim().max(200).optional()
});
var goalFieldsSchema = z.object({
  name: z.string().trim().min(1).max(120),
  icon: z.string().trim().max(8).optional(),
  colorIndex: colorIndexSchema.optional(),
  priority: z.number().int().min(0),
  targetAmount: minorUnitsSchema.min(1),
  targetDate: isoDateSchema.optional()
});
var goalSchema = goalFieldsSchema.extend({
  _id: idSchema,
  householdId: idSchema,
  contributions: z.array(goalContributionSchema),
  linkedTemplateLineId: idSchema.optional(),
  linkedDepositId: idSchema.optional(),
  status: goalStatusSchema
});
var createGoalBodySchema = goalFieldsSchema;
var patchGoalBodySchema = goalFieldsSchema.partial().extend({
  linkedTemplateLineId: idSchema.optional(),
  linkedDepositId: idSchema.optional(),
  status: goalStatusSchema.optional()
});
var notificationTypeSchema = z.enum(NOTIFICATION_TYPES);
var notificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
var emailDigestModeSchema = z.enum(EMAIL_DIGEST_MODES);
var notificationDeliverySchema = z.object({
  channel: notificationChannelSchema,
  at: instantSchema,
  ok: z.boolean(),
  error: z.string().optional()
});
var notificationEntitySchema = z.object({ type: z.string(), id: idSchema });
var notificationSchema = z.object({
  _id: idSchema,
  householdId: idSchema,
  /** undefined = every member of the household. */
  userId: idSchema.optional(),
  type: notificationTypeSchema,
  dedupeKey: z.string().min(1),
  title: z.string(),
  body: z.string(),
  entity: notificationEntitySchema.optional(),
  url: z.string().optional(),
  dueAt: instantSchema,
  readAt: instantSchema.optional(),
  deliveries: z.array(notificationDeliverySchema)
});
var notificationSettingsSchema = z.object({
  inApp: z.object({ enabled: z.literal(true) }),
  email: z.object({ enabled: z.boolean(), mode: emailDigestModeSchema }),
  discord: z.object({
    enabled: z.boolean(),
    // `webhookUrlEnc` is deliberately not part of this schema — docs/02 §2.13: "never returned
    // to clients (only `configured: true` + label)". The service layer decrypts/encrypts it
    // directly against the Mongoose document; encryption itself is P3-07's scope
    // (docs/06-roadmap.md), not P3-06's — this schema only carries what P3-06 actually reads/
    // writes (the metadata fields).
    label: z.string().trim().max(80).optional(),
    mentionRoleId: z.string().trim().max(40).optional(),
    events: z.array(notificationTypeSchema)
  }),
  // Unlike email/discord, "on this device" isn't a household-level fact — each browser that
  // subscribes gets its own row in the pushSubscriptions collection (P4-02), keyed by
  // householdId + endpoint, not stored here. This flag only gates whether the deliveries job
  // attempts webpush *at all* for the household, same shape as email/discord's own `enabled`.
  webPush: z.object({ enabled: z.boolean() })
});
var patchNotificationSettingsBodySchema = z.object({
  email: z.object({
    enabled: z.boolean().optional(),
    mode: emailDigestModeSchema.optional()
  }).optional(),
  discord: z.object({
    enabled: z.boolean().optional(),
    webhookUrl: z.url().optional(),
    label: z.string().trim().max(80).optional(),
    mentionRoleId: z.string().trim().max(40).optional(),
    events: z.array(notificationTypeSchema).optional()
  }).optional(),
  webPush: z.object({ enabled: z.boolean().optional() }).optional()
});
var testableNotificationChannelSchema = z.enum(["discord", "email"]);
var testNotificationChannelBodySchema = z.object({
  channel: testableNotificationChannelSchema
});
var pushSubscriptionKeysSchema = z.object({
  p256dh: z.string().min(1),
  auth: z.string().min(1)
});
var createPushSubscriptionBodySchema = z.object({
  endpoint: z.url(),
  keys: pushSubscriptionKeysSchema
});
var deletePushSubscriptionBodySchema = z.object({ endpoint: z.url() });

// src/domain/month.ts
function unclassifiedCountOf(line) {
  if (line.source.type !== "credit_card") return 0;
  const detail = line.computed?.detail;
  return detail?.unclassifiedCount ?? 0;
}
function lineActual(l) {
  return l.status === "skipped" ? 0 : l.actualAmount ?? 0;
}
function lineProjected(l) {
  if (l.status === "paid") return lineActual(l);
  if (l.status === "skipped") return 0;
  return l.plannedAmount;
}
function moneyTriple(lines) {
  return {
    planned: sumMinor(lines.map((l) => l.plannedAmount)),
    actual: sumMinor(lines.map(lineActual)),
    projected: sumMinor(lines.map(lineProjected))
  };
}
function moneyStat(amount, income) {
  return { amount, ratio: ratio(amount, income) };
}
function deviationEntry(target, stat) {
  const amount = target.amount - stat.amount;
  const pct = target.ratio - stat.ratio;
  return { amount, pct, action: amount < 0 ? "decrease" : amount > 0 ? "increase" : "on_target" };
}
function buildSections(expenseLines) {
  const order = [];
  const byKey = /* @__PURE__ */ new Map();
  for (const l of expenseLines) {
    if (!l.bucketKey) continue;
    const key = `${l.bucketKey}\0${l.section}\0${l.subsection ?? ""}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.lines.push(l);
    } else {
      const entry = {
        bucketKey: l.bucketKey,
        section: l.section,
        subsection: l.subsection,
        lines: [l]
      };
      byKey.set(key, entry);
      order.push(entry);
    }
  }
  return order.map((entry) => ({
    bucketKey: entry.bucketKey,
    section: entry.section,
    subsection: entry.subsection,
    ...moneyTriple(entry.lines)
  }));
}
function buildProgress(expenseLines) {
  const counted = expenseLines.filter((l) => l.status !== "skipped");
  const paid = counted.filter((l) => l.status === "paid");
  return {
    totalCount: counted.length,
    paidCount: paid.length,
    plannedAmount: sumMinor(counted.map((l) => l.plannedAmount)),
    paidAmount: sumMinor(paid.map(lineActual))
  };
}
function analyseMonth(month, buckets) {
  const incomeLines = month.lines.filter((l) => l.kind === "income");
  const expenseLines = month.lines.filter((l) => l.kind === "expense");
  const income = moneyTriple(incomeLines);
  const expense = moneyTriple(expenseLines);
  const remaining = {
    planned: income.planned - expense.planned,
    actual: income.actual - expense.actual,
    projected: income.projected - expense.projected
  };
  const bucketsOut = {};
  for (const b of buckets) {
    const bucketLines = expenseLines.filter((l) => l.bucketKey === b.key);
    const total = moneyTriple(bucketLines);
    const forRule = {
      planned: total.planned + (b.absorbsSpillOver ? remaining.planned : 0),
      actual: total.actual + (b.absorbsSpillOver ? remaining.actual : 0),
      projected: total.projected + (b.absorbsSpillOver ? remaining.projected : 0)
    };
    const planned = moneyStat(forRule.planned, income.planned);
    const actual = moneyStat(forRule.actual, income.actual);
    const projected = moneyStat(forRule.projected, income.projected);
    const target = { ratio: b.targetPct / 100, amount: pctOf(income.planned, b.targetPct) };
    bucketsOut[b.key] = {
      planned,
      actual,
      projected,
      target,
      deviation: {
        planned: deviationEntry(target, planned),
        actual: deviationEntry(target, actual)
      }
    };
  }
  const unclassifiedCount = month.lines.reduce((sum, l) => sum + unclassifiedCountOf(l), 0);
  const warnings = unclassifiedCount > 0 ? [{ code: "unclassified_card_transactions", count: unclassifiedCount }] : [];
  return {
    income,
    expense,
    remaining,
    buckets: bucketsOut,
    sections: buildSections(expenseLines),
    progress: buildProgress(expenseLines),
    warnings
  };
}
function isDueIn(recurrence, period) {
  switch (recurrence.cadence) {
    case "on_demand":
      return false;
    case "monthly":
      return true;
    case "yearly":
      return monthOfPeriod(period) === monthOfPeriod(recurrence.anchorPeriod) && comparePeriods(period, recurrence.anchorPeriod) >= 0;
    case "half_yearly":
      return diffMonths(recurrence.anchorPeriod, period) % 6 === 0 && comparePeriods(period, recurrence.anchorPeriod) >= 0;
    case "quarterly":
      return diffMonths(recurrence.anchorPeriod, period) % 3 === 0 && comparePeriods(period, recurrence.anchorPeriod) >= 0;
    case "custom":
      return recurrence.months.includes(monthOfPeriod(period));
  }
}

// src/domain/recurring.ts
var CADENCE_MONTHS = { monthly: 1, quarterly: 3, half_yearly: 6, yearly: 12 };
function isRecurringDueIn(rp, period) {
  if (rp.status !== "active") return false;
  if (rp.cadence === "monthly") return true;
  return rp.nextDueDate !== void 0 && periodFromIsoDate(rp.nextDueDate) === period;
}
function nextDueDateAfter(rp) {
  if (rp.cadence === "monthly" || rp.cadence === "one_time") {
    throw new RangeError(`nextDueDateAfter does not apply to cadence "${rp.cadence}"`);
  }
  if (!rp.nextDueDate) throw new RangeError("nextDueDateAfter requires nextDueDate to be set");
  return addMonthsToIsoDate(rp.nextDueDate, CADENCE_MONTHS[rp.cadence]);
}
function annualisedAmount(rp) {
  if (rp.cadence === "one_time") return rp.amount;
  return rp.amount * (12 / CADENCE_MONTHS[rp.cadence]);
}

// src/domain/emi.ts
function installmentNumber(startPeriod, period) {
  return diffMonths(startPeriod, period) + 1;
}
function isEmiDueIn(emi2, period) {
  const n = installmentNumber(emi2.startPeriod, period);
  return n >= 1 && n <= emi2.tenureMonths;
}
function emiRemaining(emi2, period) {
  const n = installmentNumber(emi2.startPeriod, period);
  const remainingInstallments = Math.max(0, emi2.tenureMonths - n);
  return remainingInstallments * emi2.monthlyAmount;
}

// src/domain/import.ts
function normaliseTitle(title) {
  return title.trim().toLowerCase().replace(/\s+/g, " ");
}
function fnv1a(input) {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
function transactionFingerprint(cardAccountId, date, title, amount) {
  return fnv1a(`${cardAccountId}|${date ?? ""}|${normaliseTitle(title)}|${amount}`);
}
function importTargetPeriod(statementDate, uploadDate, cardStatementDay) {
  if (statementDate) return nextPeriod(periodFromIsoDate(statementDate));
  const effectiveDate = uploadDate;
  const day = dayOfIsoDate(effectiveDate);
  const month = periodFromIsoDate(effectiveDate);
  return day >= cardStatementDay ? nextPeriod(month) : month;
}
function matchesEmi(title, amount, emi2) {
  if (Math.abs(amount) !== emi2.monthlyAmount) return false;
  const normalisedTitle = normaliseTitle(title);
  const normalisedName = normaliseTitle(emi2.name);
  return normalisedTitle.includes(normalisedName) || normalisedName.includes(normalisedTitle);
}

// src/domain/classification.ts
function classificationPattern(title) {
  return title.toLowerCase().replace(/[0-9]/g, " ").replace(/[^\p{L}\s]/gu, " ").trim().split(/\s+/).filter(Boolean).slice(0, 3).join(" ");
}

// src/domain/loan.ts
function monthlyRate(annualRatePct) {
  return annualRatePct / 12 / 100;
}
function emi(principal, annualRatePct, tenureMonths) {
  assertMinorUnits(principal, "principal");
  const r = monthlyRate(annualRatePct);
  if (r === 0) return Math.round(principal / tenureMonths);
  const factor = Math.pow(1 + r, tenureMonths);
  return Math.round(principal * r * factor / (factor - 1));
}
var MAX_SCHEDULE_MONTHS = 1200;
function rateForPeriod(rateHistory, period) {
  let best;
  for (const entry of rateHistory) {
    if (comparePeriods(periodFromIsoDate(entry.effectiveDate), period) > 0) continue;
    if (!best || entry.effectiveDate > best.effectiveDate) best = entry;
  }
  return (best ?? rateHistory[0])?.annualRatePct ?? 0;
}
function averageRateForPeriod(history, period) {
  const days = new Date(
    Date.UTC(Number(period.slice(0, 4)), Number(period.slice(5, 7)), 0)
  ).getUTCDate();
  const sorted = [...history].sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate));
  let sum = 0;
  for (let day = 1; day <= days; day++) {
    const date = `${period}-${String(day).padStart(2, "0")}`;
    const rate = sorted.filter((r) => r.effectiveDate <= date).at(-1) ?? sorted[0];
    sum += rate?.annualRatePct ?? 0;
  }
  return sum / days;
}
function prepaymentForPeriod(payments, period) {
  const matches = payments.filter(
    (p) => p.kind === "prepayment" && periodFromIsoDate(p.date) === period
  );
  if (matches.length === 0) return void 0;
  return { amount: sumMinor(matches.map((p) => p.amount)), mode: matches.at(-1)?.mode };
}
function loanSchedule(loan, until) {
  const startPeriod = periodFromIsoDate(loan.startDate);
  const rows = [];
  let opening = loan.principal;
  let currentEmi = loan.emiAmount;
  let monthsElapsed = 0;
  let targetEndMonth = loan.tenureMonths;
  while (opening > 0 && monthsElapsed < MAX_SCHEDULE_MONTHS) {
    const period = addMonths(startPeriod, monthsElapsed);
    const ratePct = rateForPeriod(loan.rateHistory, period);
    const resets = loan.rateHistory.filter(
      (r) => r.effectiveDate >= loan.startDate && periodFromIsoDate(r.effectiveDate) === period
    ).sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate));
    for (const reset of resets) {
      if (reset.repaymentMode === "set_emi" && reset.revisedEmi !== void 0)
        currentEmi = reset.revisedEmi;
      if (reset.repaymentMode === "recalculate_emi" && reset.targetRemainingMonths) {
        currentEmi = emi(opening, reset.annualRatePct, reset.targetRemainingMonths);
        targetEndMonth = monthsElapsed + reset.targetRemainingMonths;
      }
    }
    const interest = Math.round(
      opening * monthlyRate(averageRateForPeriod(loan.rateHistory, period))
    );
    const prepay = prepaymentForPeriod(loan.payments, period);
    const prepaymentAmount = prepay?.amount ?? 0;
    let principal = currentEmi - interest;
    if (principal + prepaymentAmount > opening) principal = Math.max(0, opening - prepaymentAmount);
    const closing = Math.max(0, opening - principal - prepaymentAmount);
    rows.push({
      period,
      opening,
      emi: currentEmi,
      interest,
      principal,
      prepayment: prepaymentAmount,
      closing,
      ratePct,
      negativeAmortization: interest > currentEmi
    });
    if (prepay?.mode === "reduce_emi" && closing > 0) {
      const remainingMonths = targetEndMonth - (monthsElapsed + 1);
      if (remainingMonths > 0) currentEmi = emi(closing, ratePct, remainingMonths);
    }
    opening = closing;
    monthsElapsed += 1;
  }
  const payoffPeriod = rows.at(-1)?.closing === 0 ? rows.at(-1)?.period : void 0;
  const totalInterest = sumMinor(rows.map((r) => r.interest));
  const visibleRows = until ? rows.filter((r) => comparePeriods(r.period, until) <= 0) : rows;
  const lastVisible = visibleRows.at(-1);
  return {
    rows: visibleRows,
    outstanding: lastVisible ? lastVisible.closing : loan.principal,
    interestPaid: sumMinor(visibleRows.map((r) => r.interest)),
    principalPaid: sumMinor(visibleRows.map((r) => r.principal + r.prepayment)),
    remainingMonths: rows.length - visibleRows.length,
    payoffPeriod,
    totalInterest
  };
}
function applyScenario(loan, scenario) {
  switch (scenario.type) {
    case "extraMonthly":
      return { ...loan, emiAmount: loan.emiAmount + scenario.amount };
    case "newEmi":
      return { ...loan, emiAmount: scenario.amount };
    case "lumpSum":
      return {
        ...loan,
        payments: [
          ...loan.payments,
          { date: scenario.date, amount: scenario.amount, kind: "prepayment", mode: scenario.mode }
        ]
      };
    case "rateChange":
      return {
        ...loan,
        rateHistory: [
          ...loan.rateHistory,
          { effectiveDate: scenario.from, annualRatePct: scenario.annualRatePct }
        ]
      };
  }
}
function simulateLoan(loan, scenario) {
  const baseline = loanSchedule(loan);
  const scenarioResult = loanSchedule(applyScenario(loan, scenario));
  return {
    baseline,
    scenario: scenarioResult,
    monthsSaved: baseline.rows.length - scenarioResult.rows.length,
    interestSaved: baseline.totalInterest - scenarioResult.totalInterest,
    payoffPeriod: scenarioResult.payoffPeriod
  };
}

// src/investment-tenure.ts
function investmentMaturityDate(start, months, days = 0) {
  const date = /* @__PURE__ */ new Date(`${addMonthsToIsoDate(start, months)}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

// src/domain/deposit.ts
function fdMaturityAmount(principal, annualRatePct, tenureMonths) {
  assertMinorUnits(principal, "principal");
  const quarters = tenureMonths / 3;
  const quarterlyRate = annualRatePct / 400;
  return Math.round(principal * Math.pow(1 + quarterlyRate, quarters));
}
function rdMaturityAmount(installment, annualRatePct, tenureMonths, tenureDays = 0, startDate) {
  assertMinorUnits(installment, "installment");
  if (tenureDays > 0 && startDate) {
    const end = investmentMaturityDate(startDate, tenureMonths, tenureDays);
    let total = 0;
    for (let n = 0; n <= tenureMonths + Math.ceil(tenureDays / 28); n++) {
      const contributionDate = addMonthsToIsoDate(startDate, n);
      if (contributionDate >= end) break;
      const days = (Date.parse(end) - Date.parse(contributionDate)) / 864e5;
      total += installment * Math.pow(1 + annualRatePct / 400, days * 4 / 365);
    }
    return Math.round(total);
  }
  if (tenureDays > 0) {
    const base = rdMaturityAmount(installment, annualRatePct, tenureMonths);
    return Math.round(
      (base + installment) * Math.pow(1 + annualRatePct / 400, tenureDays * 4 / 365)
    );
  }
  const quarters = tenureMonths / 3;
  const i = annualRatePct / 400;
  if (i === 0) return installment * tenureMonths;
  const factor = (Math.pow(1 + i, quarters) - 1) / (1 - Math.pow(1 + i, -1 / 3));
  return Math.round(installment * factor);
}

// src/domain/hand-loan.ts
function handLoanOutstanding(amount, repayments) {
  return amount - sumMinor(repayments.map((r) => r.amount));
}

// src/domain/goal.ts
function goalProgress(saved, targetAmount) {
  return targetAmount > 0 ? saved / targetAmount : 0;
}
function goalMonthlyNeeded(saved, targetAmount, targetDate, asOfDate) {
  const remaining = targetAmount - saved;
  if (remaining <= 0) return 0;
  const months = Math.max(
    1,
    diffMonths(periodFromIsoDate(asOfDate), periodFromIsoDate(targetDate))
  );
  return Math.ceil(remaining / months);
}

// src/domain/notification.ts
function reminderOffsetDueToday(dueDateIso, reminderDaysBefore, todayIso) {
  for (const offset of reminderDaysBefore) {
    if (addDaysToIsoDate(dueDateIso, -offset) === todayIso) return offset;
  }
  return void 0;
}
function paletteColorFor(type, paletteId = DEFAULT_PALETTE) {
  const index = NOTIFICATION_TYPES.indexOf(type);
  return oklchToInt(slotColor(getPalette(paletteId), index));
}

// src/statement-category.ts
function statementCategory(description) {
  if (/\b(?:gst|cgst|sgst|igst|utgst|service\s+tax|goods\s+(?:and|&)\s+services\s+tax)\b/i.test(
    description
  ))
    return "Taxes";
  if (/\b(?:finance\s+charges?|interest\s+(?:charges?|on|amount)|late\s+(?:payment\s+)?fees?|annual\s+(?:membership\s+)?fees?|joining\s+fees?|processing\s+fees?|forex\s+(?:markup|charges?)|overlimit\s+(?:fees?|charges?)|cash\s+advance\s+fees?)\b/i.test(
    description
  ))
    return "Bank charges";
  return void 0;
}
var investmentExitSchema = z.object({
  key: z.string().uuid(),
  date: z.iso.date(),
  incomeMonth: z.enum(["current", "next"]),
  grossAmount: minorUnitsSchema.min(0),
  fees: minorUnitsSchema.min(0).default(0),
  tax: minorUnitsSchema.min(0).default(0),
  reason: z.enum(["matured", "early_exit", "sold", "stopped"]),
  note: z.string().trim().max(500).default("")
}).refine((v) => v.fees + v.tax <= v.grossAmount, {
  message: "Fees and tax cannot exceed gross proceeds.",
  path: ["grossAmount"]
});
function investmentExit(input) {
  return {
    ...input,
    amount: input.grossAmount - input.fees - input.tax,
    incomeDate: input.incomeMonth === "next" ? addMonthsToIsoDate(input.date, 1) : input.date
  };
}

// src/card-payment.ts
function isBillPayment(tx) {
  if (tx.amount >= 0) return false;
  const title = tx.title.replace(/([a-z])([A-Z])/g, "$1 $2");
  if (/\b(?:reversal|reversed|refund|cashback|chargeback)\b/i.test(title)) return false;
  return /\b(?:credit\s*card\s*payment|cc\s+payment|bill\s+payment|payment\s*(?:received|credited|thank(?:s|\s*you)?|[-:]\s*thank)|autopay|auto\s*debit|payment\s*[-:]?\s*(?:net\s*banking|neft|imps|upi|bbps))\b/i.test(
    title
  );
}

// src/card-reconciliation.ts
function cardEntryKind(row) {
  if (row.entryKind) return row.entryKind;
  if (row.amount >= 0) return "purchase";
  if (isBillPayment(row)) return "repayment";
  return /\b(?:refund|reversal|reversed|fuel|surcharge|chargeback)\b/i.test(row.title) ? "refund" : "other_credit";
}
function reconcileCardBill(statement, entries) {
  entries = [...new Map(entries.map((entry) => [entry.id, entry])).values()];
  const aliases = /* @__PURE__ */ new Set([statement.id, ...statement.aliases ?? []]);
  const included = new Set(statement.rows.map((row) => row.transactionId).filter(Boolean));
  const sum = (rows) => rows.reduce((n, row) => n + row.amount, 0);
  const debits = sum(statement.rows.filter((row) => row.amount > 0));
  const credits = -sum(statement.rows.filter((row) => row.amount < 0));
  const repayments = -sum(statement.rows.filter((row) => cardEntryKind(row) === "repayment"));
  const refunds = -sum(statement.rows.filter((row) => cardEntryKind(row) === "refund"));
  const otherCredits = -sum(
    statement.rows.filter((row) => row.amount < 0 && cardEntryKind(row) === "other_credit")
  );
  const calculatedBalance = statement.previousBalance === void 0 ? void 0 : statement.previousBalance + debits - credits;
  const statementBalance = statement.totalAmountDue;
  const after = entries.filter(
    (row) => !included.has(row.id) && row.postingStatus !== "pending" && row.afterStatementId && aliases.has(row.afterStatementId) && !!row.evidence?.trim()
  );
  let applied = 0;
  const appliedRows = [];
  for (const row of after) {
    if (row.amount >= 0) continue;
    const amount = Math.min(
      -row.amount,
      (row.billApplications ?? []).filter((a) => aliases.has(a.statementId) && a.evidence.trim()).reduce((n, a) => n + a.amount, 0)
    );
    if (amount > 0) {
      applied += amount;
      appliedRows.push({ id: row.id, title: row.title, amount, kind: cardEntryKind(row) });
    }
  }
  const pending = entries.filter((row) => row.postingStatus === "pending" && !included.has(row.id));
  const unknownCutoff = entries.filter(
    (row) => row.postingStatus !== "pending" && !included.has(row.id) && !after.some((a) => a.id === row.id)
  );
  const unallocated = entries.filter(
    (row) => row.amount < 0 && row.postingStatus !== "pending" && (row.billApplications ?? []).filter((a) => a.evidence.trim()).reduce((n, a) => n + a.amount, 0) < -row.amount
  );
  const afterDebits = sum(after.filter((row) => row.amount > 0));
  const afterCredits = -sum(after.filter((row) => row.amount < 0));
  const remaining = statementBalance === void 0 ? void 0 : Math.max(0, statementBalance - applied);
  const postedBalance = statementBalance === void 0 ? void 0 : statementBalance + afterDebits - afterCredits;
  return {
    statementId: statement.id,
    debits,
    credits,
    repayments,
    refunds,
    otherCredits,
    netSpending: debits - refunds,
    // Manual entries affect spending immediately; unknown cutoff must not imply bill allocation.
    recordedNetSpending: debits - refunds + entries.filter((row) => !included.has(row.id)).reduce((n, row) => n + cardSpendingAmount(row), 0),
    knownNetSpending: debits - refunds + after.reduce((n, row) => n + cardSpendingAmount(row), 0),
    calculatedBalance,
    statementBalance,
    reconciliationDifference: statementBalance === void 0 || calculatedBalance === void 0 ? void 0 : calculatedBalance - statementBalance,
    debitDifference: statement.purchasesCharges === void 0 ? void 0 : debits - statement.purchasesCharges - (statement.cashAdvances ?? 0),
    creditDifference: statement.paymentsCredits === void 0 ? void 0 : credits - statement.paymentsCredits,
    appliedAfterCutoff: applied,
    appliedRows,
    remainingBillDue: remaining,
    excessAppliedCredit: statementBalance === void 0 ? void 0 : Math.max(0, applied - Math.max(0, statementBalance)),
    postedAfterCutoffDebits: afterDebits,
    postedAfterCutoffCredits: afterCredits,
    postedBalance,
    currentOutstanding: postedBalance === void 0 ? void 0 : Math.max(0, postedBalance),
    creditBalance: postedBalance === void 0 ? void 0 : Math.max(0, -postedBalance),
    pendingDebits: sum(pending.filter((row) => row.amount > 0)),
    pendingCredits: -sum(pending.filter((row) => row.amount < 0)),
    unknownCutoffIds: unknownCutoff.map((row) => row.id),
    unallocatedCreditIds: unallocated.map((row) => row.id)
  };
}
function cardSpendingAmount(row) {
  if (row.postingStatus === "pending") return 0;
  const kind = cardEntryKind({
    amount: row.amount,
    title: row.title,
    entryKind: row.entryKind ?? void 0
  });
  return kind === "purchase" || kind === "refund" ? row.amount : 0;
}

// src/template-due-day.ts
function templateDueDay(period, day, rule) {
  if (rule === "none") return void 0;
  if (rule !== "last_working_day") return day;
  const [year, month] = period.split("-").map(Number);
  const date = new Date(Date.UTC(year, month, 0));
  while (date.getUTCDay() === 0 || date.getUTCDay() === 6) date.setUTCDate(date.getUTCDate() - 1);
  return date.getUTCDate();
}
var holdingSchema = z.object({
  pricing: z.enum(["automatic", "manual"]),
  exchange: z.enum(["NSE", "BSE"]).optional(),
  symbol: z.string().trim().toUpperCase().regex(/^[A-Z0-9&.-]{1,30}$/).optional(),
  schemeCode: z.string().regex(/^\d{4,8}$/).optional(),
  isin: z.string().trim().toUpperCase().max(12).optional(),
  units: z.number().finite().min(0).max(1e9),
  costBasis: z.number().int().min(0).max(1e14),
  asOf: z.iso.date(),
  manualPrice: z.number().finite().positive().max(1e9).optional(),
  manualPriceDate: z.iso.date().optional()
}).superRefine((v, ctx) => {
  if (v.units === 0 !== (v.costBasis === 0))
    ctx.addIssue({
      code: "custom",
      message: "Units and cost must both be zero or both be positive."
    });
  if (v.pricing === "manual" && (!v.manualPrice || !v.manualPriceDate))
    ctx.addIssue({ code: "custom", message: "Enter a manual price and its date." });
});
function holdingPosition(holding, events) {
  let units = holding.units, costBasis = holding.costBasis, realisedGain = 0, unallocated = 0;
  for (const e of [...events].filter((e2) => e2.date > holding.asOf).sort((a, b) => a.date.localeCompare(b.date))) {
    if (!e.units) {
      unallocated++;
      continue;
    }
    if (!Number.isFinite(e.units) || e.units <= 0 || !Number.isSafeInteger(e.amount) || e.amount <= 0)
      throw new Error("Invalid holding activity.");
    if (e.direction === "out") {
      units += e.units;
      costBasis += e.amount;
    } else {
      if (e.units > units + 1e-8)
        throw new Error("Cannot sell more units than held on this date.");
      const released = e.units >= units - 1e-8 ? costBasis : Math.round(costBasis * e.units / units);
      realisedGain += e.amount - released;
      costBasis -= released;
      units = Math.max(0, units - e.units);
    }
    units = Math.round(units * 1e8) / 1e8;
    if (!Number.isSafeInteger(costBasis) || costBasis > 1e14 || units > 1e9)
      throw new Error("Holding exceeds supported limits.");
  }
  return {
    units,
    costBasis,
    realisedGain,
    unallocated,
    averageCost: units ? costBasis / units / 100 : null
  };
}
function holdingValuation(holding, events, quote) {
  const position = holdingPosition(holding, events);
  const raw = quote && !position.unallocated ? Math.round(position.units * quote.price * 100) : null;
  const marketValue = raw !== null && Number.isSafeInteger(raw) ? raw : null;
  const unrealisedGain = marketValue === null ? null : marketValue - position.costBasis;
  return {
    ...position,
    marketValue,
    unrealisedGain,
    returnPct: unrealisedGain === null || !position.costBasis ? null : unrealisedGain / position.costBasis * 100,
    quote
  };
}
var ONBOARDING_STEPS = [
  "welcome",
  "family",
  "accounts",
  "cards",
  "budget",
  "investments",
  "borrowings",
  "wealth",
  "month",
  "review"
];
var onboardingUpdateSchema = z.object({
  householdId: idSchema,
  step: z.enum(ONBOARDING_STEPS),
  reviewedStep: z.enum(ONBOARDING_STEPS).optional(),
  complete: z.boolean().optional()
}).strict();
var TOUR_SECTIONS = [
  "month",
  "cards",
  "investments",
  "bills",
  "accounts",
  "borrowings",
  "wealth",
  "insights",
  "documents",
  "template",
  "settings",
  "inbox"
];
var tourUpdateSchema = z.object({
  householdId: idSchema,
  section: z.enum(TOUR_SECTIONS),
  completed: z.boolean().optional()
}).strict();

export { CLASSIFICATION_RULE_SOURCES, EMI_STATUSES, IMPORT_BANK_ADAPTERS, IMPORT_FILE_TYPES, IMPORT_STATUSES, ISO_DATE_RE, ONBOARDING_STEPS, PERIOD_RE, PRIVACY_MASK_TEXT, RECURRING_STATUSES, STARTER_TEMPLATES, TOUR_SECTIONS, TRANSACTION_CLASSIFICATIONS, accountBankingSchema, accountCardSchema, accountSchema, accountTypeSchema, addDaysToIsoDate, addDepositContributionBodySchema, addGoalContributionBodySchema, addHandLoanRepaymentBodySchema, addLoanPaymentBodySchema, addLoanRateChangeBodySchema, addMonthLineBodySchema, addMonths, addMonthsToIsoDate, analyseMonth, annualisedAmount, assertMinorUnits, bucketDefSchema, bucketsSchema, bulkPatchCardTransactionRowSchema, bulkPatchCardTransactionsBodySchema, cardEntryKind, cardImportSchema, cardSpendingAmount, cardTransactionSchema, classificationPattern, classificationRuleSchema, classificationRuleSourceSchema, colorIndexSchema, commitImportBodySchema, commitImportRowSchema, comparePeriods, createAccountBodySchema, createCardTransactionBodySchema, createCardTransactionsBodySchema, createDepositBodySchema, createEmiBodySchema, createGoalBodySchema, createHandLoanBodySchema, createHouseholdBodySchema, createInviteBodySchema, createLoanBodySchema, createPushSubscriptionBodySchema, createRecurringBodySchema, createTemplateLineBodySchema, creditEmiDue, creditEmiSchedule, creditEmiTermsSchema, currencySchema, currencySymbol, dateInPeriod, dayOfIsoDate, deletePushSubscriptionBodySchema, depositContributionSchema, depositSchema, depositStatusSchema, depositTypeSchema, diffMonths, editScopeSchema, emailDigestModeSchema, emi, emiRemaining, emiSchema, emiStatusSchema, fdMaturityAmount, formatMoney, formatMoneyCompact, goalContributionSchema, goalContributionSourceSchema, goalMonthlyNeeded, goalProgress, goalSchema, goalStatusSchema, handLoanDirectionSchema, handLoanOutstanding, handLoanRepaymentSchema, handLoanSchema, handLoanStatusSchema, holdingPosition, holdingSchema, holdingValuation, householdSettingsPatchSchema, householdSettingsSchema, idSchema, importBankAdapterSchema, importFileTypeSchema, importMappingSchema, importStatsSchema, importStatusSchema, importTargetPeriod, installmentNumber, instantSchema, investmentExit, investmentExitSchema, investmentMaturityDate, isBillPayment, isDueIn, isEmiDueIn, isMinorUnits, isPeriod, isPeriodInRange, isRecurringDueIn, isoDateInZone, isoDateSchema, lineActual, lineLinkSchema, lineSourceSchema, loanPaymentKindSchema, loanPaymentSchema, prepaymentModeSchema as loanPrepaymentModeSchema, loanRateHistoryEntrySchema, loanRateTypeSchema, loanScenarioSchema, loanSchedule, loanSchema, loanStatusSchema, loanTypeSchema, markRecurringPaidBodySchema, matchesEmi, memberRoleSchema, minorUnitsSchema, monthLineSchema, monthOfPeriod, monthSchema, monthSummarySchema, nextDueDateAfter, nextPeriod, nonNegativeMinorUnitsSchema, normaliseTitle, notificationChannelSchema, notificationDeliverySchema, notificationEntitySchema, notificationSchema, notificationSettingsSchema, notificationTypeSchema, onboardingUpdateSchema, paletteColorFor, parseMoney, parsePeriod, patchAccountBodySchema, patchCardTransactionBodySchema, patchDepositBodySchema, patchEmiBodySchema, patchGoalBodySchema, patchHandLoanBodySchema, patchHouseholdBodySchema, patchLoanBodySchema, patchMonthLineBodySchema, patchNotificationSettingsBodySchema, patchRecurringBodySchema, patchTemplateLineBodySchema, pctOf, pctSchema, periodFromDate, periodFromIsoDate, periodLabel, periodRange, periodSchema, personPatchSchema, personRoleSchema, personSchema, prepaymentModeSchema, prevPeriod, previewImportBodySchema, pushSubscriptionKeysSchema, putBucketsBodySchema, ratio, rdMaturityAmount, reconcileCardBill, recurrenceSchema, recurringCadenceSchema, recurringDetailsSchema, recurringKindSchema, recurringPaymentSchema, recurringStatusSchema, reminderOffsetDueToday, requireBucketKeyForExpense, setMonthLineStatusBodySchema, simulateLoan, simulateLoanBodySchema, slugSchema, spillOverBucket, statementCategory, sumMinor, templateDueDay, templateLineSchema, testNotificationChannelBodySchema, testableNotificationChannelSchema, toMajor, toMinor, toPeriod, tourUpdateSchema, transactionClassificationSchema, transactionFingerprint, transferOwnershipBodySchema, uploadImportBodySchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map